import type {
  FacilityDto,
  FacilitySinglePageDto,
} from '@/shared/api/modules/contracts';
import type { GetPayerTypesResponseDto } from '@/shared/api/modules/facilities';
import type { FacilityStatus } from '../model/contracts';
import type { Facility, FacilitySinglePage } from '../model/types';

export const transformFacilityDtoToFacility = (dto: FacilityDto): Facility => {
  const {
    id,
    name,
    status,
    coordinates,
    bedsAvg,
    bedsHighest,
    staffAvg,
    residents,
    staffingRatio,
    payerPrivatePct,
    payerMedicaidPct,
    createdFlags,
    createdAlerts,
    occupancy,
    revenue,
    monthlyRevenue,
    revenuePerResident,
    hasPayerTypes,
    expenses,
    expensesInsufficientData,
    marginAvgPct,
    revenuePerResidentAvg,
    revenuePerBedAvg,
    payerPrivateRevenuePct,
    payerMedicaidRevenuePct,
  } = dto;

  const isCoordinatesExist =
    coordinates.lat !== null && coordinates.lng !== null;

  return {
    id: String(id),
    name: name,
    occupancy: {
      changePct: occupancy.changePct,
      currentAvg: occupancy.currentAvg,
    },
    status: status as FacilityStatus,
    bedsAvg: bedsAvg,
    bedsHighest: bedsHighest ?? null,
    staffAvg: staffAvg,
    residents: residents,
    staffingRatio: staffingRatio,
    payerPrivatePct: payerPrivatePct ?? null,
    payerMedicaidPct: payerMedicaidPct ?? null,
    hasPayerTypes: hasPayerTypes ?? false,
    createdFlags: createdFlags ?? 0,
    createdAlerts: createdAlerts ?? 0,
    revenue: revenue ?? { amountUsd: null, changePct: null },
    monthlyRevenue: monthlyRevenue ?? { amountUsd: null, changePct: null },
    revenuePerResident: revenuePerResident ?? {
      amountUsd: null,
      changePct: null,
    },
    /* Financial tab columns (ST #137). This mapper is an allow-list -- a field
       missing here is silently dropped even though the API returned it. */
    expenses: expenses ?? null,
    expensesInsufficientData: expensesInsufficientData ?? false,
    marginAvgPct: marginAvgPct ?? null,
    revenuePerResidentAvg: revenuePerResidentAvg ?? null,
    revenuePerBedAvg: revenuePerBedAvg ?? null,
    payerPrivateRevenuePct: payerPrivateRevenuePct ?? null,
    payerMedicaidRevenuePct: payerMedicaidRevenuePct ?? null,
    coordinates: isCoordinatesExist
      ? { lat: coordinates.lat || 0, lng: coordinates.lng || 0 }
      : null,
  };
};

export const transformFacilitySinglePageDtoToFacilitySinglePage = (
  dto: FacilitySinglePageDto
): FacilitySinglePage => {
  const {
    id,
    name,
    status,
    coordinates,
    address,
    phoneNumber,
    pricingModel,
    totalBeds,
    totalStaff,
    activeResidentsCount,
    types,
    yearBuilt,
    managers,
  } = dto;

  const isCoordinatesExist =
    coordinates.lat !== null && coordinates.lng !== null;

  return {
    id: String(id),
    name: name,
    status: status as FacilityStatus,
    coordinates: isCoordinatesExist
      ? { lat: coordinates.lat || 0, lng: coordinates.lng || 0 }
      : null,
    address: address,
    phoneNumber: phoneNumber,
    pricingModel: pricingModel
      ? { id: pricingModel.id, name: pricingModel.name }
      : null,
    totalBeds: totalBeds,
    totalStaff: totalStaff,
    activeResidentsCount: activeResidentsCount,
    types: types,
    yearBuilt: yearBuilt,
    managers,
  };
};

const capitalize = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

export const transformPayerTypesDtoToForm = (
  payerTypes: GetPayerTypesResponseDto | undefined
) => {
  return (
    payerTypes?.map((pt) => ({
      id: pt.id,
      name: pt.productTypeName ?? '',
      type: pt.payer
        ? { value: pt.payer, label: capitalize(pt.payer.toLowerCase()) }
        : null,
    })) ?? []
  );
};
